/**
 * Analytics service providing a bridge between the application and analytics software.
 * Dynamically switches between real and mocked implementations based on the REACT_APP_ANALYTICS environment variable.
 */

import { cloneDeep, set, merge, get } from 'lodash'
import { qs } from '../routing'

declare global {
  interface Window {
    du_digitalData?: Record<string, any> // Adjust the type based on the structure of du_digitalData
    __CJ_DEBUG_ANALYTICS?: Record<string, any>
    _satellite?: Record<string, any>
  }
}

// Type Definitions
interface DataLayer {
  [key: string]: any // Extendable for additional properties
}

interface PageInfo {
  pageName: string
  formType?: string
  formName?: string
  viewChange?: string
}

interface FormErrors {
  [field: string]: any
}

// Mock Analytics Implementation
const mock = {
  default: {} as DataLayer,
  datalayer: {} as DataLayer,
  recovery: {} as DataLayer,
  pageName: '',
  viewChange: '',
  isLive: false,

  pageBottom(): void {},

  setDefaults(obj: DataLayer): void {
    Object.assign(this.default, obj)
  },

  addToDatalayer(additions: DataLayer): void {
    Object.keys(additions).forEach(key => {
      set(this.datalayer, key, additions[key])
    })
  },

  retainTrackData(data: DataLayer): void {
    const clonedDefaultDatalayer = cloneDeep(this.default)
    this.recovery = merge(
      {},
      clonedDefaultDatalayer,
      this.datalayer,
      window.du_digitalData || {},
      data
    )
  },

  trackRetained(): void {
    this.setTrackingData({
      ...this.recovery,
      [this.keys.eventType(0)]: 'pageView',
      [this.keys.eventAction(0)]: 'Success',
    })
    this.trackPage()
  },

  setTrackingData(additions: DataLayer): void {
    const clonedDefaultDatalayer = cloneDeep(this.default)
    const trackingData: DataLayer = {}
    Object.keys(additions).forEach(key => {
      set(trackingData, key, additions[key])
    })
    window.du_digitalData = merge(
      {},
      clonedDefaultDatalayer,
      this.datalayer,
      trackingData
    )
  },

  trackViewChange(): void {
    this.track('viewChange')
  },

  trackPage(): void {
    this.track('page')
  },

  trackInteraction(): void {
    this.track('interaction')
  },

  getPageInfoObject({
    pageName,
    formType,
    formName,
    viewChange,
  }: PageInfo): DataLayer {
    return {
      [this.keys.pageName()]: pageName,
      [this.keys.formType()]: formType,
      [this.keys.formName()]: formName,
      [this.keys.viewChange()]: viewChange,
    }
  },

  trackPageView(pageInfo: DataLayer, eventAction = 'Success'): void {
    this.setTrackingData({
      ...pageInfo,
      [this.keys.eventType(0)]: 'pageView',
      [this.keys.eventAction(0)]: eventAction,
    })
    this.trackPage()
  },

  trackFormContinueButton(
    pageInfo: DataLayer,
    eventLinkInfo: string,
    eventType: string,
    eventAction = 'Provided'
  ): void {
    this.setTrackingData({
      ...pageInfo,
      [this.keys.eventLinkInformation(0)]: eventLinkInfo,
      [this.keys.eventType(0)]: eventType,
      [this.keys.eventAction(0)]: eventAction,
    })
    this.trackInteraction()
  },

  trackFormAbort(pageInfo: DataLayer, lastFieldTouched: string): void {
    this.setTrackingData({
      ...pageInfo,
      [this.keys.eventLinkInformation(0)]: 'Form abort',
      [this.keys.formLastTouchedField()]: lastFieldTouched,
      [this.keys.eventType(0)]: 'Form',
      [this.keys.eventAction(0)]: 'Aborted',
    })
    this.trackInteraction()
  },

  shouldTrack(): boolean {
    const token = qs(document.location.search, 'token')
    return !(window.location.href.includes('continue') && !token)
  },

  trackFormInputErrors(
    pageInfo: DataLayer,
    errors: FormErrors,
    linkInfo = 'Field error'
  ): void {
    this.setTrackingData({
      ...pageInfo,
      [this.keys.eventLinkInformation(0)]: linkInfo,
      [this.keys.formErrorFields()]: Object.keys(errors),
      [this.keys.eventType(0)]: 'Form',
      [this.keys.eventAction(0)]: 'Field error',
    })
    this.trackInteraction()
  },

  track(action: string): void {
    if (typeof action !== 'string') {
      console.warn('Legacy tracking code detected')
      return
    }
    if (!implementation.isLive || window.__CJ_DEBUG_ANALYTICS) {
      console.log(
        `track ${action}\n pageName: ${get(
          window.du_digitalData,
          this.keys.pageName()
        )}\n viewChange: ${get(
          window.du_digitalData,
          this.keys.viewChange()
        )}\n event0.type: ${get(
          window.du_digitalData,
          this.keys.eventType(0)
        )}\n event0.action: ${get(
          window.du_digitalData,
          this.keys.eventAction(0)
        )}\n\n Datalayer:`,
        window.du_digitalData
      )
    }
    window._satellite?.track(action)
  },

  keys: {
    pageName: () => 'core.pageInfo.pageName',
    version: () => 'core.pageInfo.version',
    releaseDate: () => 'core.pageInfo.releaseDate',
    maturityLevel: () => 'core.category.maturityLevel',
    viewChange: () => 'core.attributes.viewChange',
    eventType: (eventCount: number) =>
      `event[${eventCount}].eventInfo.eventType`,
    eventAction: (eventCount: number) =>
      `event[${eventCount}].eventInfo.eventAction`,
    eventLinkInformation: (eventCount: number) =>
      `event[${eventCount}].eventInfo.linkInformation`,
    eventTargetURL: (eventCount: number) =>
      `event[${eventCount}].eventInfo.eventTargetURL`,
    errorCode: () => 'error.errorCode',
    errorMessage: () => 'error.errorMessage',
    errorCausingURL: () => 'error.errorCausingURL',
    formErrorFields: () => 'form.errorFields',
    formType: () => 'form.type',
    formName: () => 'form.name',
    formLastTouchedField: () => 'form.lastTouchedField',
  },
}

// Real Analytics Implementation
const real = {
  ...mock,
  isLive: true,
  pageBottom(): void {
    window._satellite?.pageBottom()
  },
}

// Dynamically Switch Implementation
let implementation = mock
if (process.env.REACT_APP_ANALYTICS) {
  implementation = real
}

export { implementation as Analytics }
